import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Container, Row, Table } from 'react-bootstrap'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { ActiveInActive } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddCategory from '../../../components/modal/category/ModalAddCategory'
import ModalUpdateCategory from '../../../components/modal/category/ModalUpdateCategory'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const MasterCategory = () => {
  const headerTitleStyle = {
    color: 'var(--primary-orange)',
    fontSize: '24px',
    fontWeight: '600'
  }

  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    color: '#333333',
    fontWeight: '700',
    borderBottom: '1px solid #e9ecef',
    textTransform: 'uppercase',
    fontSize: '0.85rem'
  }

  const [modalAddShow, setModalAddShow] = useState(false)
  const [modalUpdateShow, setModalUpdateShow] = useState(false)
  const [editId, setEditId] = useState(0)
  const [categories, setCategories] = useState([])
  const [meta, setMeta] = useState(initialMeta)
  const [loading, setLoading] = useState(false)

  const { per_page, current_page } = meta
  const abortControllerRef = useRef(null)

  const loadCategory = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const { data } = await api.get('master/categories', {
        signal,
        params: { per_page, page: current_page }
      })
      setMeta(data.metadata)
      setCategories(data.data)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    loadCategory()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page])

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
    window.scrollTo(0, 0)
  }

  const handleEdit = (id) => {
    setEditId(id)
    setModalUpdateShow(true)
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const refreshData = () => loadCategory()

  return (
    <Container fluid className="px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Category</h3>
        </Col>
      </Row>

      <Row className="align-items-center mb-3">
        <Col>
          <h4
            className="text-muted text-uppercase mb-0"
            style={{ fontSize: '14px' }}
          >
            Category List
          </h4>
        </Col>
        <Col className="text-end">
          <button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            onClick={() => setModalAddShow(true)}
          >
            Add Category
          </button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table hover responsive className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle">Category Name</th>
                  <th className="p-2 align-middle text-end">Status</th>
                  <th className="p-2 align-middle text-end">Created</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && !categories.length && (
                  <tr>
                    <td colSpan={4}>
                      <RowEmpty colSpan={4} />
                    </td>
                  </tr>
                )}
                {!loading &&
                  categories.map((data, i) => (
                    <tr key={data.id || i}>
                      <td className="p-2 align-middle fw-medium text-dark">
                        {data?.name}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <ActiveInActive
                          text={data.status ? 'active' : 'inactive'}
                        />
                      </td>
                      <td className="p-2 align-middle text-end text-muted">
                        {convertDateWithTime(data?.createdAt)}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <TableAction>
                          <button
                            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => handleEdit(data.id)}
                          >
                            <Pencil size={13} />
                          </button>
                        </TableAction>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {categories.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handlePageChange={handlePageChange}
                  handleOnChangeMeta={handleOnChangeMeta}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ModalAddCategory
        show={modalAddShow}
        onHide={() => setModalAddShow(false)}
        refreshData={refreshData}
      />

      <ModalUpdateCategory
        show={modalUpdateShow}
        onHide={() => setModalUpdateShow(false)}
        id={editId}
        refreshData={refreshData}
      />
    </Container>
  )
}

export default MasterCategory
